/* Minimal synchronous store. NOT a port — the old app had no store
 * abstraction at all (STATE/ACT_STATUS/FLAGGED were bare mutable module
 * globals, mutated in place and re-read by whichever render* function ran
 * next). This is the smallest wrapper that gives payload.ts/ui.ts a
 * `get/set/update/subscribe` shape without changing that synchronous
 * mutate-then-render behavior: set()/update() notify listeners
 * synchronously, in the same tick, before returning — a caller that reads
 * the store immediately after set() always sees the new value, same as
 * assigning STATE = ... did.
 */
export type Unsubscribe = () => void;

export interface Store<T> {
  get(): T;
  set(value: T): void;
  update(fn: (value: T) => T): void;
  subscribe(listener: (value: T) => void): Unsubscribe;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<(value: T) => void>();
  const notify = () => { listeners.forEach((l) => l(value)); };
  return {
    get: () => value,
    set(next: T) {
      value = next;
      notify();
    },
    update(fn: (value: T) => T) {
      value = fn(value);
      notify();
    },
    subscribe(listener: (value: T) => void): Unsubscribe {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}
